import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import './FeedbackForm.css';

interface FeedbackData {
  name: string;
  role: string;
  belt: string;
  email: string;
  feedbackText: string;
}

const FeedbackForm: React.FC = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<FeedbackData>({
    name: '',
    role: '',
    belt: '',
    email: '',
    feedbackText: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const encode = (data: Record<string, string>) =>
    Object.keys(data)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({
        'form-name': 'feedback',
        name: formData.name,
        role: formData.role,
        belt: formData.belt,
        email: formData.email,
        feedback: formData.feedbackText,
        rating: String(rating),
      }),
    })
      .then(() => setSubmitted(true))
      .catch(error => console.error('Error submitting feedback:', error));
  };

  if (submitted) {
    return (
      <section className="feedback-page">
        <div className="feedback-container">
          <div className="feedback-success">
            <div className="feedback-success-icon">★</div>
            <h2>Thank you for your feedback!</h2>
            <p>We really appreciate you taking the time to share your experience. The instructor will review your submission.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="feedback-page">
      <div className="feedback-container">
        <h1 className="feedback-page-title">Share Your Experience</h1>
        <p className="feedback-page-subtitle">
          We'd love to hear from you — your feedback helps us improve and lets others know what to expect from Weald Taekwon-Do.
        </p>

        <form
          className="feedback-form"
          name="feedback"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="form-name" value="feedback" />
          <div hidden><input name="bot-field" /></div>

          <div className="feedback-form-row">
            <div className="feedback-form-group">
              <label htmlFor="fb-name">Your Name *</label>
              <input
                id="fb-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g. Jane Smith"
              />
            </div>
            <div className="feedback-form-group">
              <label htmlFor="fb-role">I am a *</label>
              <select
                id="fb-role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="">Please select...</option>
                <option value="Student">Student</option>
                <option value="Parent / Guardian">Parent / Guardian</option>
              </select>
            </div>
          </div>

          <div className="feedback-form-row">
            <div className="feedback-form-group">
              <label htmlFor="fb-belt">Belt colour (yours or your child's)</label>
              <input
                id="fb-belt"
                type="text"
                name="belt"
                value={formData.belt}
                onChange={handleChange}
                placeholder="e.g. Yellow Belt"
              />
            </div>
            <div className="feedback-form-group">
              <label htmlFor="fb-email">Email (optional — if you'd like a response)</label>
              <input
                id="fb-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. jane@example.com"
              />
            </div>
          </div>

          <div className="feedback-form-group">
            <label>Your Rating *</label>
            <div className="feedback-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`feedback-star ${star <= (hoverRating || rating) ? 'active' : ''}`}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                >★</button>
              ))}
            </div>
          </div>

          <div className="feedback-form-group">
            <label htmlFor="fb-feedback">Your Feedback *</label>
            <textarea
              id="fb-feedback"
              name="feedbackText"
              value={formData.feedbackText}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Tell us about your experience at Weald Taekwon-Do..."
            />
          </div>

          <button type="submit" className="feedback-submit" disabled={rating === 0}>
            Submit Feedback
          </button>
          {rating === 0 && (
            <p className="feedback-rating-hint">Please select a star rating before submitting.</p>
          )}
        </form>
      </div>
    </section>
  );
};

export default FeedbackForm;
